import { useEffect, useState } from 'react';
import { t } from '@/data/translations';
import type { Lang } from '@/pages/Index';

const sectionIds = ['header', 'profile', 'experience', 'education', 'skills', 'projects', 'gallery', 'contact'] as const;

const ProgressIndicator = ({ lang }: { lang: Lang }) => {
  const [activeSection, setActiveSection] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const tr = t(lang).nav;

  const sections = sectionIds.map(id => ({
    id,
    label: tr[id as keyof typeof tr],
  }));

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setScrollProgress(progress);

      const isAtBottom = scrollTop + window.innerHeight >= document.documentElement.scrollHeight - 50;
      if (isAtBottom) {
        setActiveSection(sections.length - 1);
        return;
      }

      const sectionElements = sections.map(s => document.getElementById(s.id));
      const viewportMiddle = scrollTop + window.innerHeight / 3;

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const el = sectionElements[i];
        if (el && el.offsetTop <= viewportMiddle) {
          setActiveSection(i);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSectionClick = (index: number) => {
    const el = document.getElementById(sections[index].id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="fixed left-6 top-1/2 -translate-y-1/2 z-40 hidden sidebar:flex flex-col items-center gap-0">
      <div className="absolute left-1/2 -translate-x-1/2 w-px h-full bg-border/30" />
      <div 
        className="absolute left-1/2 -translate-x-1/2 w-px bg-primary/40 origin-top transition-all duration-300"
        style={{ height: `${scrollProgress}%` }}
      />
      <div className="relative flex flex-col gap-6">
        {sections.map((section, index) => (
          <button
            key={section.id}
            onClick={() => handleSectionClick(index)}
            className="group relative flex items-center"
            aria-label={section.label}
          >
            <div 
              className={`
                w-2 h-2 rounded-full transition-all duration-300 ease-out
                group-hover:scale-150
                ${index === activeSection 
                  ? 'bg-primary scale-150 shadow-[0_0_8px_hsl(var(--primary)/0.5)]' 
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }
              `}
            />
            <span 
              className={`
                absolute left-5 whitespace-nowrap text-xs font-medium
                transition-all duration-300 ease-out
                group-hover:scale-110 group-hover:translate-x-0.5 origin-left
                ${index === activeSection ? 'text-primary' : 'text-muted-foreground'}
              `}
            >
              {section.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ProgressIndicator;
